<template>
  <div class="price-system-page">
    <template v-if="skuId">
      <div class="price-system-head">
        <strong class="price-system-title">仓库价格体系</strong>
        <div v-if="!readonly" class="price-system-actions">
          <a-button size="small" :disabled="!selectedKeys.length" @click="batchDelete">删除仓库</a-button>
          <a-button size="small" type="primary" ghost :disabled="!canAdd" @click="addRow">+添加</a-button>
        </div>
      </div>

      <a-spin :spinning="loading">
        <a-table
          class="warehouse-price-table"
          row-key="rowKey"
          size="middle"
          bordered
          :pagination="false"
          :columns="displayColumns"
          :data-source="rows"
          :row-selection="readonly ? null : { selectedRowKeys: selectedKeys, onChange: onSelectChange }"
          :locale="{ emptyText: readonly ? '当前规格尚未配置仓库价格' : '当前规格尚未添加仓库，请点击右上角“+添加”' }"
        >
          <template slot="warehouse" slot-scope="text, record">
            <a-select
              v-if="record._draft && !readonly"
              :value="record.warehouseId"
              size="small"
              placeholder="请选择"
              style="width:100%"
              :disabled="record._saving"
              @change="value => confirmDraftWarehouse(record, value)"
            >
              <a-select-option v-for="item in availableWarehouseOptions" :key="item.id" :value="item.id">
                {{ item.warehouseName }}
              </a-select-option>
            </a-select>
            <span v-else>{{ record.warehouseName || '—' }}</span>
          </template>
          <template slot="price" slot-scope="text, record">
            <div class="base-price-with-case">
              <a-input-number
                v-model="record.draftGuidePrice"
                :min="0"
                :precision="2"
                size="small"
                class="base-price-input"
                :disabled="readonly || record._draft || record._saving"
                placeholder="基本单位价"
                @blur="saveRow(record)"
              />
              <span class="case-rate-tag" title="来自规格「单位配置」箱规，只读">箱规 {{ caseRateLabel }}</span>
            </div>
          </template>
          <template slot="salePrice" slot-scope="text, record">
            <div class="sale-price-with-hint">
              <span class="sale-price-value">{{ salePriceText(record) }}</span>
              <span
                v-if="salePriceFormulaHint(record)"
                class="sale-price-formula"
                :title="salePriceFormulaTitle">
                {{ salePriceFormulaHint(record) }}
              </span>
            </div>
          </template>
          <template slot="mall" slot-scope="text, record">
            <div class="mall-checks">
              <a-checkbox :checked="isMallOn(record, 'huazhuMallListed')" :disabled="readonly || record._draft || record._saving" @change="e => toggleMall(record, 'huazhuMallListed', e.target.checked)">华住</a-checkbox>
              <a-checkbox :checked="isMallOn(record, 'meituanListed')" :disabled="readonly || record._draft || record._saving" @change="e => toggleMall(record, 'meituanListed', e.target.checked)">美团</a-checkbox>
              <a-checkbox :checked="isMallOn(record, 'selfMallListed')" :disabled="readonly || record._draft || record._saving" @change="e => toggleMall(record, 'selfMallListed', e.target.checked)">自营</a-checkbox>
            </div>
          </template>
          <template slot="ops" slot-scope="text, record">
            <a v-if="!readonly" class="danger-link" @click="removeRow(record)">删除</a>
            <span v-else>—</span>
          </template>
        </a-table>
      </a-spin>
    </template>
    <a-empty v-else description="请先选择或保存商品 SKU，再维护价格体系" />
  </div>
</template>

<script>
import { listWarehouse } from '@/api/erp/warehouse'
import {
  listGuidePrice,
  saveGuidePrice,
  addGuidePriceWarehouse,
  deleteGuidePriceWarehouse
} from '@/api/erp/guidePrice'

export default {
  name: 'GuidePriceEditor',
  props: {
    skuId: { type: [String, Number], default: undefined },
    readonly: { type: Boolean, default: false },
    saleRate: { type: Number, default: 1 },
    saleUnitName: { type: String, default: '' },
    /** 规格单位配置 · 箱规（只读展示，随商品档案单位配置联动） */
    caseRate: { type: [Number, String], default: 1 },
    /** 基本单位名称（用于换算说明） */
    baseUnitName: { type: String, default: '' }
  },
  data () {
    return {
      warehouses: [],
      prices: [],
      draftRows: [],
      loading: false,
      selectedKeys: [],
      columns: [
        { title: '仓库名称', dataIndex: 'warehouseName', width: 180, scopedSlots: { customRender: 'warehouse' } },
        { title: '基本单位价(元)', dataIndex: 'draftGuidePrice', width: 220, scopedSlots: { customRender: 'price' } },
        { title: '销售单位价(元)', dataIndex: 'salePrice', width: 340, scopedSlots: { customRender: 'salePrice' } },
        { title: '上架商城', dataIndex: 'mall', scopedSlots: { customRender: 'mall' } },
        { title: '操作项', dataIndex: 'ops', width: 90, scopedSlots: { customRender: 'ops' } }
      ]
    }
  },
  computed: {
    displayColumns () {
      if (!this.readonly) return this.columns
      return this.columns.filter(col => col.dataIndex !== 'ops')
    },
    savedRows () {
      const groups = {}
      this.prices.forEach(row => {
        const key = String(row.warehouseId)
        if (!groups[key]) groups[key] = row
      })
      return Object.values(groups)
    },
    rows () {
      return this.readonly ? this.savedRows : this.savedRows.concat(this.draftRows)
    },
    usedWarehouseIds () {
      return new Set(this.savedRows.map(row => String(row.warehouseId)))
    },
    availableWarehouseOptions () {
      return this.warehouses.filter(item => !this.usedWarehouseIds.has(String(item.id)))
    },
    canAdd () {
      return !this.readonly && this.availableWarehouseOptions.length > 0
    },
    caseRateLabel () {
      const n = Number(this.caseRate)
      if (!isFinite(n) || n <= 0) return '1'
      return String(parseFloat(n.toFixed(6)))
    },
    salePriceFormulaTitle () {
      const baseUnit = this.baseUnitName || '基本单位'
      const saleUnit = this.saleUnitName || baseUnit
      const rate = Number(this.saleRate) > 0 ? Number(this.saleRate) : 1
      if (rate === 1) {
        return `销售单位与基本单位一致（${saleUnit}）：销售单位价 = 基本单位价`
      }
      return `销售单位为整箱（${saleUnit}）：销售单位价 = 基本单位价 × 箱规；1${saleUnit} = ${this.caseRateLabel}${baseUnit}`
    }
  },
  watch: {
    skuId () { this.loadAll() }
  },
  created () {
    this.loadAll()
  },
  methods: {
    mapPrice (row) {
      const huazhu = row.huazhuMallListed === true || row.huazhuMallListed === 1 || row.huazhuMallListed === '1'
      const meituan = row.meituanListed === true || row.meituanListed === 1 || row.meituanListed === '1'
      const selfMall = row.selfMallListed === true || row.selfMallListed === 1 || row.selfMallListed === '1'
      return {
        ...row,
        rowKey: 'wh-' + row.warehouseId,
        huazhuMallListed: huazhu,
        meituanListed: meituan,
        selfMallListed: selfMall,
        draftGuidePrice: Number(row.guidePrice || 0),
        saleUnitGuidePrice: row.saleUnitGuidePrice != null ? Number(row.saleUnitGuidePrice) : null,
        _originalHuazhu: huazhu,
        _originalMeituan: meituan,
        _originalSelf: selfMall,
        _saving: false
      }
    },
    loadAll () {
      this.selectedKeys = []
      this.draftRows = []
      if (!this.skuId) {
        this.prices = []
        return
      }
      this.loading = true
      Promise.all([listWarehouse(true), listGuidePrice(this.skuId)]).then(([warehouseRes, priceRes]) => {
        this.warehouses = warehouseRes.data || []
        this.prices = (priceRes.data || []).map(this.mapPrice)
      }).finally(() => { this.loading = false })
    },
    onSelectChange (keys) {
      this.selectedKeys = keys
    },
    salePriceText (row) {
      if (!row || row._draft) return '—'
      if (row.draftGuidePrice == null || row.draftGuidePrice === '') return '—'
      // 随规格「单位配置」中的销售单位/箱规实时换算，不用接口里的 saleUnitGuidePrice（按已保存默认销售单位计算）
      const rate = Number(this.saleRate) > 0 ? Number(this.saleRate) : 1
      const price = (Number(row.draftGuidePrice) * rate).toFixed(2)
      return this.saleUnitName ? `${price} 元/${this.saleUnitName}` : price
    },
    salePriceFormulaHint (row) {
      if (row && row._draft) return ''
      const rate = Number(this.saleRate) > 0 ? Number(this.saleRate) : 1
      const baseUnit = this.baseUnitName || '基本单位'
      if (rate === 1) {
        return `= ${baseUnit}价`
      }
      const base = row && row.draftGuidePrice != null && row.draftGuidePrice !== ''
        ? Number(row.draftGuidePrice)
        : NaN
      if (isFinite(base)) {
        return `= ${base.toFixed(2)}×${this.caseRateLabel}`
      }
      return `= ${baseUnit}价×箱规${this.caseRateLabel}`
    },
    isMallOn (row, field) {
      const value = row && row[field]
      return value === true || value === 1 || value === '1'
    },
    addRow () {
      if (!this.canAdd) {
        this.$message.warning('当前SKU已添加全部可用仓库')
        return
      }
      if (this.draftRows.some(row => row._draft && row.warehouseId == null)) {
        this.$message.warning('请先选择上一行的仓库')
        return
      }
      this.draftRows.push({
        rowKey: 'draft-' + Date.now(),
        warehouseId: undefined,
        warehouseName: '',
        draftGuidePrice: undefined,
        huazhuMallListed: true,
        meituanListed: false,
        selfMallListed: false,
        _draft: true,
        _saving: false
      })
    },
    confirmDraftWarehouse (record, warehouseId) {
      const warehouse = this.availableWarehouseOptions.find(item => String(item.id) === String(warehouseId))
      if (!warehouse) return this.$message.warning('请选择仓库')
      this.$set(record, '_saving', true)
      addGuidePriceWarehouse({
        skuId: this.skuId,
        warehouseId: warehouse.id
      }).then(res => {
        const created = Array.isArray(res) ? res : ((res && res.data) || [])
        const row = created[0]
        if (!row || !row.id) return
        const needFix = !this.isMallOn(row, 'huazhuMallListed')
          || this.isMallOn(row, 'meituanListed')
          || this.isMallOn(row, 'selfMallListed')
        if (!needFix) return
        return saveGuidePrice({
          id: row.id,
          skuId: row.skuId,
          warehouseId: row.warehouseId,
          guidePriceName: row.guidePriceName || '销售指导价',
          guidePrice: Number(row.guidePrice || 0).toFixed(2),
          selfMallListed: false,
          huazhuMallListed: true,
          meituanListed: false
        })
      }).then(() => {
        this.$message.success(`仓库“${warehouse.warehouseName}”已添加`)
        this.loadAll()
      }).finally(() => { this.$set(record, '_saving', false) })
    },
    saveRow (row) {
      if (!row || row._draft || row._saving) return
      if (row.draftGuidePrice == null || Number(row.draftGuidePrice) < 0) {
        row.draftGuidePrice = Number(row.guidePrice || 0)
        return this.$message.warning('当前价格不能小于 0')
      }
      const priceChanged = Number(row.draftGuidePrice).toFixed(2) !== Number(row.guidePrice || 0).toFixed(2)
      const mallChanged = !!row.huazhuMallListed !== !!row._originalHuazhu
        || !!row.meituanListed !== !!row._originalMeituan
        || !!row.selfMallListed !== !!row._originalSelf
      if (!priceChanged && !mallChanged) return
      this.$set(row, '_saving', true)
      saveGuidePrice({
        id: row.id,
        skuId: row.skuId,
        warehouseId: row.warehouseId,
        guidePriceName: row.guidePriceName || '销售指导价',
        guidePrice: Number(row.draftGuidePrice).toFixed(2),
        selfMallListed: !!row.selfMallListed,
        huazhuMallListed: !!row.huazhuMallListed,
        meituanListed: !!row.meituanListed
      }).then(() => {
        this.$message.success('价格已保存')
        this.loadAll()
      }).catch(() => { this.loadAll() }).finally(() => { this.$set(row, '_saving', false) })
    },
    toggleMall (row, field, checked) {
      this.$set(row, field, checked)
      this.saveRow(row)
    },
    removeRow (record) {
      if (record._draft) {
        this.draftRows = this.draftRows.filter(row => row.rowKey !== record.rowKey)
        this.selectedKeys = this.selectedKeys.filter(key => key !== record.rowKey)
        return
      }
      this.$confirm({
        title: '确认删除仓库',
        content: `确定删除仓库“${record.warehouseName}”及当前规格下的价格吗？`,
        okText: '确认删除',
        okType: 'danger',
        onOk: () => deleteGuidePriceWarehouse({ skuId: this.skuId, warehouseId: record.warehouseId }).then(() => {
          this.$message.success('仓库价格已删除')
          this.loadAll()
        })
      })
    },
    batchDelete () {
      const targets = this.rows.filter(row => this.selectedKeys.indexOf(row.rowKey) >= 0)
      if (!targets.length) return
      const drafts = targets.filter(row => row._draft)
      const saved = targets.filter(row => !row._draft)
      const runDrafts = () => {
        drafts.forEach(row => this.removeRow(row))
      }
      if (!saved.length) {
        runDrafts()
        return
      }
      this.$confirm({
        title: '确认删除仓库',
        content: `确定删除已选的 ${saved.length} 个仓库及其价格吗？`,
        okText: '确认删除',
        okType: 'danger',
        onOk: () => Promise.all(saved.map(row => deleteGuidePriceWarehouse({
          skuId: this.skuId,
          warehouseId: row.warehouseId
        }))).then(() => {
          runDrafts()
          this.$message.success('仓库价格已删除')
          this.loadAll()
        })
      })
    }
  }
}
</script>

<style scoped>
.price-system-page{padding:4px 0 12px}
.price-system-head{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:12px}
.price-system-title{font-size:14px;font-weight:600;color:#1f2937}
.price-system-actions{display:flex;align-items:center;gap:8px}
.warehouse-price-table /deep/ .ant-table-thead>tr>th{background:#fafafa;color:rgba(0,0,0,.85);font-weight:500}
.mall-checks{display:flex;align-items:center;gap:12px;white-space:nowrap}
.mall-checks /deep/ .ant-checkbox-wrapper{margin:0}
.danger-link{color:#f5222d;cursor:pointer}
.base-price-with-case{display:flex;align-items:center;gap:8px;width:100%}
.base-price-with-case .base-price-input{flex:1;min-width:0}
.case-rate-tag{flex:0 0 auto;font-size:12px;color:#8792a5;white-space:nowrap;user-select:none}
.sale-price-with-hint{display:flex;align-items:center;gap:6px;flex-wrap:nowrap;white-space:nowrap;width:100%;min-width:0}
.sale-price-value{flex:0 0 auto;font-weight:500;color:#1f2937;white-space:nowrap}
.sale-price-formula{flex:0 0 auto;font-size:12px;color:#8792a5;white-space:nowrap}
.warehouse-price-table /deep/ .ant-table-tbody>tr>td{vertical-align:middle}
.warehouse-price-table /deep/ td .sale-price-with-hint{overflow:hidden;text-overflow:ellipsis}
</style>
